import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Check, Plus, X } from 'lucide-react';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './ui/command';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const TagSelect = ({ value = [], onChange, placeholder = "Select tags..." }) => {
    const [open, setOpen] = useState(false);
    const [tags, setTags] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(false);
    const [creating, setCreating] = useState(false);

    useEffect(() => {
        fetchTags();
    }, []);

    const fetchTags = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API_URL}/tags`);
            setTags(response.data);
        } catch (error) {
            console.error('Failed to fetch tags:', error);
        } finally {
            setLoading(false);
        }
    };

    const createTag = async (tagName) => {
        try {
            setCreating(true);
            const response = await axios.post(`${API_URL}/tags`, { name: tagName });
            const newTag = response.data;
            // Refresh tags list to ensure we have the latest
            await fetchTags();
            return newTag;
        } catch (error) {
            console.error('Failed to create tag:', error);
            throw error;
        } finally {
            setCreating(false);
        }
    };

    const handleSelect = async (tagId) => {
        if (value.includes(tagId)) {
            // Remove tag if already selected
            onChange(value.filter(id => id !== tagId));
        } else {
            // Add tag
            onChange([...value, tagId]);
        }
    };

    const handleCreate = async () => {
        if (!searchQuery.trim()) return;

        try {
            const newTag = await createTag(searchQuery.trim());
            onChange([...value, newTag.id]);
            setSearchQuery('');
            setOpen(false);
        } catch (error) {
            console.error('Failed to create tag:', error);
        }
    };

    const handleRemove = (tagId, e) => {
        e.stopPropagation();
        onChange(value.filter(id => id !== tagId));
    };

    const selectedTags = tags.filter(tag => tag && value.includes(tag.id));
    const filteredTags = tags.filter(tag =>
        tag &&
        tag.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
    // Show create option if search query is not empty and doesn't exactly match any existing tag (case-insensitive)
    // Also ensure it shows even if there are partial matches in filteredTags
    const trimmedQuery = searchQuery.trim();
    const showCreateOption = trimmedQuery.length > 0 &&
        !tags.some(tag => tag && tag.name.toLowerCase() === trimmedQuery.toLowerCase());

    const getTagColor = (tag) => {
        if (!tag || !tag.color) return { bg: '#e5e7eb', text: '#374151' };
        return {
            bg: tag.color + '20', // 20% opacity
            text: tag.color
        };
    };

    return (
        <div className="space-y-2">
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={open}
                        className="w-full justify-start min-h-[40px] h-auto py-2"
                    >
                        {selectedTags.length === 0 ? (
                            <span className="text-muted-foreground">{placeholder}</span>
                        ) : (
                            <div className="flex flex-wrap gap-1.5 w-full">
                                {selectedTags.map((tag) => {
                                    const colors = getTagColor(tag);
                                    return (
                                        <Badge
                                            key={tag.id}
                                            variant="secondary"
                                            className="text-xs px-2 py-0.5 rounded-md font-medium"
                                            style={{
                                                backgroundColor: colors.bg,
                                                color: colors.text,
                                                borderColor: colors.text + '40'
                                            }}
                                        >
                                            {tag.name}
                                            <button
                                                onClick={(e) => handleRemove(tag.id, e)}
                                                className="ml-1.5 hover:bg-black/10 rounded-full p-0.5"
                                            >
                                                <X className="h-3 w-3" />
                                            </button>
                                        </Badge>
                                    );
                                })}
                            </div>
                        )}
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[300px] p-0" align="start">
                    <Command>
                        <CommandInput
                            placeholder="Search tags..."
                            value={searchQuery}
                            onValueChange={setSearchQuery}
                        />
                        <CommandList>
                            {loading ? (
                                <div className="py-6 text-center text-sm text-muted-foreground">
                                    Loading tags...
                                </div>
                            ) : (
                                <>
                                    {filteredTags.length === 0 && !showCreateOption ? (
                                        <CommandEmpty>No tags found.</CommandEmpty>
                                    ) : (
                                        <CommandGroup>
                                            {filteredTags.map((tag) => {
                                                const colors = getTagColor(tag);
                                                return (
                                                    <CommandItem
                                                        key={tag.id}
                                                        value={tag.name}
                                                        onSelect={() => handleSelect(tag.id)}
                                                        className="cursor-pointer"
                                                    >
                                                        <div className="flex items-center gap-2 w-full">
                                                            <div
                                                                className="w-3 h-3 rounded-full flex-shrink-0"
                                                                style={{ backgroundColor: tag.color }}
                                                            />
                                                            <span className="flex-1">{tag.name}</span>
                                                            {value.includes(tag.id) && (
                                                                <Check className="h-4 w-4" />
                                                            )}
                                                        </div>
                                                    </CommandItem>
                                                );
                                            })}
                                            {showCreateOption && (
                                                <CommandItem
                                                    value={searchQuery}
                                                    onSelect={handleCreate}
                                                    className="cursor-pointer text-primary"
                                                    disabled={creating}
                                                >
                                                    <Plus className="h-4 w-4 mr-2" />
                                                    {creating ? 'Creating...' : `Create "${trimmedQuery}"`}
                                                </CommandItem>
                                            )}
                                        </CommandGroup>
                                    )}
                                </>
                            )}
                        </CommandList>
                    </Command>
                </PopoverContent>
            </Popover>
        </div>
    );
};

export default TagSelect;
